import { NgClass, NgOptimizedImage, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InlineSVGModule } from '@digilize/shared/ui/inline-svg/src';
import { TranslocoModule } from '@jsverse/transloco';
import { catchError, combineLatest, finalize, first, Observable, of, Subject, take, takeUntil } from 'rxjs';

import { CartState } from '../../application/states/cart.state';
import { CheckoutState } from '../../application/states/checkout.state';
import { ShowState } from '../../application/states/show.state';
import { hasOptions, type Supplement } from '../../models/product';
import { AllergenListComponent } from '../allergen-list/allergen-list.component';
import { ConfiguredItemsComponent } from '../configured-items/configured-items.component';
import { OptionsQuantityComponent } from '../configured-items/options-quantity.component';
import { LocaleCurrencyPipe } from '../locale-currency.pipe';
import { ResizePipe } from '../media/resize.pipe';
import { ProductOptionsService } from '../product-options-dialog/product-options.service';
import { QuantityControlComponent } from '../quantity-control/quantity-control.component';

interface SupplementLimit {
  canAddMore: boolean;
  max: number | null;
}

/**
 * The extras of the film and its format, which hang under no ticket: on the supplements page, or below
 * all tickets when the cinema set them to the ticket page (PAUL-3884). "Max 1 per ticket" counts every ticket.
 */
@Component({
  selector: 'plys-film-extras',
  templateUrl: './film-extras.component.html',
  styleUrls: ['./film-extras.component.scss', '../../styles/list-item.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AsyncPipe,
    NgClass,
    NgOptimizedImage,
    NgTemplateOutlet,
    MatTooltipModule,
    TranslocoModule,
    InlineSVGModule,
    LocaleCurrencyPipe,
    ResizePipe,
    AllergenListComponent,
    QuantityControlComponent,
    OptionsQuantityComponent,
    ConfiguredItemsComponent,
  ],
})
export class FilmExtrasComponent implements OnDestroy {
  @Input({ required: true }) supplements: Supplement[] = [];

  protected readonly reserving$ = this.cartState.reserving$;
  protected readonly menuUrl$ = this.showState.showMenuRedirectUrl$;
  protected readonly hasOptions = hasOptions;
  private readonly expandDetails: Record<string, boolean> = {};
  private readonly limits = new Map<string, Observable<SupplementLimit>>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cartState: CartState,
    private readonly checkoutState: CheckoutState,
    private readonly showState: ShowState,
    private readonly productOptions: ProductOptionsService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** `control` is the clicked one: a refused add never sets reserving, so nothing else stops its spinner (PAUL-3885). */
  addToCart(product: Supplement, supplements: Supplement[], control?: QuantityControlComponent): void {
    this.cartState
      .addItem(product, supplements, product.mainProductId)
      .pipe(
        take(1), // An add that reserves nothing completes empty: `first()` would turn that into an error.
        takeUntil(this.destroy$),
        finalize(() => control?.settle()),
        catchError((err: Error) => {
          this.checkoutState.setError(err.message);
          return of(null);
        })
      )
      .subscribe();
  }

  openOptions(product: Supplement, supplements: Supplement[]): void {
    this.productOptions
      .addItem(product, supplements, product.mainProductId)
      .pipe(
        take(1), // A closed dialog emits nothing: end quietly rather than as an error.
        takeUntil(this.destroy$),
        catchError((err: Error) => {
          // A refused reservation has already reached the snackbar through the error interceptor.
          if (!(err instanceof HttpErrorResponse)) this.checkoutState.setError(err.message);
          return of(null);
        })
      )
      .subscribe();
  }

  removeFromCart(id: string): void {
    this.cartState.removeItem(id).pipe(first(), takeUntil(this.destroy$)).subscribe();
  }

  canAddMore(product: Supplement): Observable<boolean> {
    return this.cartState.canAddMore(product);
  }

  /**
   * Whether + may add another, and the cap the "maximum" hint names (PAUL-3883). Cached per listed row so
   * the async pipe keeps one subscription.
   */
  limitOf(product: Supplement): Observable<SupplementLimit> {
    const key = `${product.id}:${product.mainProductId ?? ''}`;
    let limit$ = this.limits.get(key);
    if (!limit$) {
      limit$ = combineLatest({
        canAddMore: this.cartState.canAddMore(product),
        max: this.cartState.maxFor(product),
      });
      this.limits.set(key, limit$);
    }
    return limit$;
  }

  getItemQuantity(productId: string): Observable<number> {
    return this.cartState.getItemQuantity(productId);
  }

  toggleDetails(productId: string, supplementId: string = ''): void {
    this.expandDetails[productId + supplementId] = !this.expandDetails[productId + supplementId];
  }

  isExpanded(productId: string, supplementId: string = ''): boolean {
    return this.expandDetails[productId + supplementId] || false;
  }

  isMenuVisible(supplement: Supplement): boolean {
    return supplement.hasReservation;
  }
}
